import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api, { refreshSession } from "../../services/api";
import toastError from "../../errors/toastError";

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});

	useEffect(() => {
		const onExpired = () => { setIsAuth(false); setUser({}); };
		window.addEventListener("auth:expired", onExpired);
		return () => window.removeEventListener("auth:expired", onExpired);
	}, []);

	useEffect(() => {
		const token = localStorage.getItem("token");
		(async () => {
			if (token) {
				try {
					const data = await refreshSession();
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
					setIsAuth(true);
					setUser(data.user);
					localStorage.setItem("user", JSON.stringify(data.user));
				} catch (err) {
					toastError(err);
				}
			}
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		if (!isAuth || !user?.id) return;
		const socket = openSocket();

		socket.on("user", data => {
			if (data.action === "update" && data.user.id === user.id) {
				const updatedUser = {
					...data.user,
					permissions: data.user.permissions || user.permissions || [],
				};
				setUser(updatedUser);
				localStorage.setItem("user", JSON.stringify(updatedUser));
			}
		});

		socket.on("profile", data => {
			if (data.action === "update" && data.profile?.key === user.profile) {
				const extraPermissions = Array.isArray(user.extraPermissions)
					? user.extraPermissions
					: [];
				const updatedUser = {
					...user,
					permissions: [
						...new Set([...(data.profile.permissions || []), ...extraPermissions]),
					],
				};
				setUser(updatedUser);
				localStorage.setItem("user", JSON.stringify(updatedUser));
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user, isAuth]);

	const handleLogin = async userData => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/login", userData);
			localStorage.setItem("token", JSON.stringify(data.token));
			localStorage.setItem("user", JSON.stringify(data.user));
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setIsAuth(true);
			toast.success(i18n.t("auth.toasts.success"));
			history.push("/tickets");
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			await api.delete("/auth/logout");
			setIsAuth(false);
			setUser({});
			localStorage.removeItem("token");
			localStorage.removeItem("user");
			api.defaults.headers.Authorization = undefined;
			setLoading(false);
			history.push("/login");
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	return { isAuth, user, loading, handleLogin, handleLogout };
};

export default useAuth;
